import type { SgtmPixelConfig } from '@/services/sgtmPixelConfig';
import { ProviderType } from './types';

export type { SgtmPixelConfig };

/**
 * BFF 下发的单个平台配置
 */
interface TrackingPlatformConfig {
  enabled: boolean;
}

/**
 * BFF 通过 jsonvalues.tracking_config 下发的完整配置
 * key 与 ProviderType 枚举值一一对应
 */
export interface TrackingConfig {
  version?: string;
  [key: string]: TrackingPlatformConfig | string | undefined;
}

/**
 * sgtm_pixel_config key → ProviderType 枚举值的映射表
 * axon / stape / pinterest 暂无对应 Provider，预留忽略
 */
const SGTM_KEY_TO_PROVIDER: Partial<Record<keyof SgtmPixelConfig, ProviderType>> = {
  ga4: ProviderType.GA4,
  awin: ProviderType.AWIN,
  bing: ProviderType.BING,
  criteo: ProviderType.CRITEO,
  tiktok: ProviderType.TIKTOK,
  twitter: ProviderType.TWITTER,
  facebook: ProviderType.FACEBOOK,
  rtbhouse: ProviderType.RTB_HOUSE,
  snapchat: ProviderType.SNAPCHAT,
  snowplow: ProviderType.SNOWPLOW,
};

/**
 * 将 sgtm_pixel_config 的 boolean 格式转为 TrackingConfig 格式
 * { facebook: true } → { facebook: { enabled: true } }
 */
export function parseSgtmPixelConfig(config: SgtmPixelConfig | null | undefined): TrackingConfig {
  if (!config) return {};

  const result: TrackingConfig = {};

  (Object.keys(config) as Array<keyof SgtmPixelConfig>).forEach((key) => {
    const providerType = SGTM_KEY_TO_PROVIDER[key];
    if (providerType !== undefined && typeof config[key] === 'boolean') {
      result[providerType] = { enabled: config[key] as boolean };
    }
  });

  return result;
}

/**
 * 解析 BFF 下发的 tracking_config，获取指定平台是否启用
 *
 * @param trackingConfig - BFF jsonvalues 中的 tracking_config 对象
 * @param providerType - Provider 类型（与 ProviderType 枚举值一致）
 * @returns boolean | undefined - 若配置中有明确值则返回 boolean，否则返回 undefined
 */
export function getTrackingEnabled(
  trackingConfig: TrackingConfig | undefined | null,
  providerType: ProviderType,
): boolean | undefined {
  if (!trackingConfig) return undefined;

  const platformConfig = trackingConfig[providerType];
  if (platformConfig && typeof platformConfig === 'object' && 'enabled' in platformConfig) {
    return platformConfig.enabled;
  }
  return undefined;
}

/**
 * 合并多层配置源，确定最终的 isEnabled 值
 *
 * 优先级: runtimeConstraint > sgtmPixelConfig > trackingConfig > envVar > defaultValue
 *
 * @param options.runtimeConstraint - 运行时硬约束（如 isInZeloolApp），false 时直接禁用
 * @param options.sgtmPixelConfigEnabled - SGTM 像素开关（10分钟轮询，最高远程优先级）
 * @param options.trackingConfigEnabled - BFF 下发的 tracking_config 配置
 * @param options.envEnabled - 环境变量值
 * @param options.defaultEnabled - 默认值（默认 true）
 */
export function resolveProviderEnabled(options: {
  runtimeConstraint?: boolean;
  sgtmPixelConfigEnabled?: boolean;
  trackingConfigEnabled?: boolean;
  envEnabled: boolean;
  defaultEnabled?: boolean;
}): boolean {
  const {
    runtimeConstraint,
    sgtmPixelConfigEnabled,
    trackingConfigEnabled,
    envEnabled,
    defaultEnabled = true,
  } = options;

  // 运行时硬约束优先（如 APP 内禁用）
  if (runtimeConstraint === false) return false;

  // SGTM 像素开关（服务端 10 分钟轮询，高于 BFF tracking_config）
  if (sgtmPixelConfigEnabled !== undefined) return sgtmPixelConfigEnabled;

  // BFF 下发的 tracking_config 次之
  if (trackingConfigEnabled !== undefined) return trackingConfigEnabled;

  // 环境变量，若为 undefined 则降级到默认值
  return envEnabled ?? defaultEnabled;
}
